import { useTheme, type Palette } from '@mui/material/styles';
import { getIconComponent, type IconName } from '@sm-dashboard/ui-icons';
import { resolvePaletteColor, type ThemePaletteColor } from '../paletteColorPath';

export type IconSize = 'small' | 'medium' | 'large';

export type IconColor = ThemePaletteColor | 'inherit' | 'black' | 'white';

export type IconProps = {
  /** Registered icon name from `@sm-dashboard/ui-icons`. */
  icon: IconName;
  /** @default 'medium' */
  size?: IconSize;
  /** Theme palette path, e.g. `primary.main`, `inherit`, `black`, or `white`. */
  color?: IconColor;
  titleAccess?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
};

function resolveIconColor(palette: Palette, color: IconColor): string | undefined {
  if (color === 'inherit') return undefined;
  if (color === 'white') return palette.common.white;
  if (color === 'black') return palette.common.black;
  return resolvePaletteColor(palette, color);
}

export function Icon({
  icon,
  size = 'medium',
  color = 'inherit',
  titleAccess,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden
}: IconProps) {
  const theme = useTheme();
  const IconComponent = getIconComponent(icon);
  const isDecorative = !ariaLabel && !titleAccess;
  const resolvedColor = resolveIconColor(theme.palette, color);

  return (
    <IconComponent
      className={['SmIcon-root', `SmIcon-size-${size}`].join(' ')}
      titleAccess={titleAccess}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden ?? isDecorative}
      {...(resolvedColor ? { htmlColor: resolvedColor, style: { color: resolvedColor } } : { color: 'inherit' })}
    />
  );
}
